import wr527Fudges from "./wr527Fudges";
import {
    FormStateConsts,
    websiteStages
} from "../FormDesigner/Form/DataCapture";
import toBoolean from "../../lib/iprs-react-library/src/util/toBoolean";

const DEV_WEB_CONFIG_ID         = 34566456;
const PROD_WEB_CONFIG_ID        = 34763845;
const vitalitWysaConfigIDs      = [1780520280, 1857861110]; // cases via Wysa

/**
 * Hardcoded field options for DOB and address fields.
 * These override unreliable backend data to ensure the config system works.
 *
 * For address fields, each array element is a group of related address fields.
 * The dropdown shows the first field's key as the identifier for the group.
 */
const getHardcodedFieldOptions = () => ({
    datetextbox: [
        [
            {
                key: "DOB",
                defaultText: "BirthDate"
            }
        ],
        [
            {
                key: "DateJoined",
                defaultText: "Date joined company"
            }
        ],
        [
            {
                key: "DateJoinedCompany",
                defaultText: "Date joined company"
            }
        ],
        [
            {
                key: "DateOfAccident",
                defaultText: "Date of Injury"
            }
        ],
        [
            {
                key: "DateOfInjury",
                defaultText: "Date of Injury"
            }
        ],
        [
            {
                key: "PolicyRenewalDate",
                defaultText: "Policy Renewal Date"
            }
        ],
        [
            {
                key: "RTWDate",
                defaultText: "Return to work date (if any)"
            }
        ],
        [
            {
                key: "StartDateOfCurrentAbsence",
                defaultText: "Start date of current absence"
            }
        ],
        [
            {
                key: "TreatmentFinishDate",
                defaultText: "Treatment Finish Date"
            }
        ]
    ],
    address: [
        // Standard Address
        [
            {
                key: "Addr1",
                defaultText: "Address1"
            },
            {
                key: "Addr2",
                defaultText: "Address2"
            },
            {
                key: "Addr3",
                defaultText: "Address3"
            },
            {
                key: "County",
                defaultText: "County"
            },
            {
                key: "Postcode",
                defaultText: "Postcode"
            },
            {
                key: "Town",
                defaultText: "Town"
            }
        ],
        // Work Address
        [
            {
                key: "WorkAddress1",
                defaultText: "Address1"
            },
            {
                key: "WorkAddress2",
                defaultText: "Address2"
            },
            {
                key: "WorkPostcode",
                defaultText: "Postcode"
            },
            {
                key: "WorkCounty",
                defaultText: "County"
            },
            {
                key: "WorkTown",
                defaultText: "Town"
            }
        ],
        // Solicitor Address
        [
            {
                key: "SolicitorAddress",
                defaultText: "Address1"
            },
            {
                key: "SolicitorAddress2",
                defaultText: "Address2"
            },
            {
                key: "SolicitorPostcode",
                defaultText: "Postcode"
            },
            {
                key: "SolicitorCounty",
                defaultText: "County"
            },
            {
                key: "SolicitorTown",
                defaultText: "Town"
            }
        ]
    ]
});

const updateWithConfig = (webConfig, newDC) => {

    if(DEV_WEB_CONFIG_ID === webConfig?.WebsiteConfigID ||
        PROD_WEB_CONFIG_ID === webConfig?.WebsiteConfigID){
        newDC.websiteConfig = wr527Fudges(webConfig);
        webConfig.ShowContactAndGPUpdate = false;
    }else if(vitalitWysaConfigIDs.includes(webConfig?.WebsiteConfigID)){

        webConfig.ShowContactAndGPUpdate = true;

        newDC.websiteConfig = webConfig;
    }else{
        // pass the whole of the config to be stored on the app state
        webConfig.ShowContactAndGPUpdate = false;
        newDC.websiteConfig = webConfig;
    }

    // Override fieldOptions with hardcoded values for DOB and address fields
    // to work around unreliable backend data
    const hardcodedOptions = getHardcodedFieldOptions();
    if (newDC.websiteConfig && newDC.websiteConfig.fieldOptions) {
        newDC.websiteConfig.fieldOptions = {
            ...newDC.websiteConfig.fieldOptions,
            datetextbox: hardcodedOptions.datetextbox,
            address: hardcodedOptions.address
        };
    } else if (newDC.websiteConfig) {
        // If fieldOptions doesn't exist at all, create it with just the hardcoded values
        newDC.websiteConfig.fieldOptions = {
            ...hardcodedOptions
        };
    }

    newDC.activeStages = [...websiteStages];
    if (newDC.configMode) {
        // Finish doesn't display in the progress bar in live site
        newDC.activeStages.push([FormStateConsts.Finish, "Finish"]);
        newDC.activeStages.push([
            FormStateConsts.Configuration,
            "Configuration"
        ]);
    }

    const Fields = newDC.websiteConfig?.ReferralForm?.Fields;

    if (Fields) {
        newDC.websiteConfig.ReferralForm = {};
        newDC.websiteConfig.ReferralForm.Fields = Fields.map((f, i) => {
            // make sure every field has a name and a lower case controlType
            const name = f.name ? f.name : "index_" + i;
            const controlTypeTmp = f.controlType
                ? f.controlType.toLowerCase()
                : "text";
            const controlType =
                "textbox" === controlTypeTmp ? "text" : controlTypeTmp;
            const required =
                "true" === f.required || true === f.required ? true : false;

            const fieldCopy = { ...f, name, controlType, required };

            if ("dropdownlist" === controlType) {
                const options = f.Options ? f.Options : f.Item;

                fieldCopy.Options = options ? options : [];
            }

            return fieldCopy;
        });
    } else {
        newDC.websiteConfig.ReferralForm = null;
    }

    const patient = newDC.websiteConfig.Patient;
    newDC.personalData = {
        ...patient,
        DOB: patient?.BirthDate ?? null,
        personTitle: patient?.PersonalTitle ?? null,
        Forename: patient?.FirstName ?? null,
        Surname: patient?.LastName ?? null
    };

    if (!newDC.Client) {
        if (1 === newDC.websiteConfig?.Clients?.length) {
            newDC.Client = newDC.websiteConfig.Clients[0];
        } else {
            newDC.Client = {};
        }
    }
    if (!newDC?.Client?.ClientPartyID) {
        newDC.Client.ClientPartyID = newDC.websiteConfig.ClientPartyID;
    }

    newDC.serviceActivityId = newDC.websiteConfig.ServiceActivityId;
    newDC.serviceId = newDC.websiteConfig?.ServiceId;

    newDC.captureInjuries = newDC.websiteConfig.CaptureInjuries;
    newDC.ServiceActivities = Array.isArray(newDC.websiteConfig?.ServiceActivities) ?
        [...newDC.websiteConfig.ServiceActivities] : [];

    newDC.appointmentTypes = newDC.websiteConfig.AppointmentTypes;
    if (1 === newDC.websiteConfig.AppointmentTypes?.length) {
        newDC.appointmentType = newDC.appointmentTypes[0];
        if (!newDC.websiteConfig.CaptureInjuries && !newDC.websiteConfig?.CaptureVisual) {
            newDC.activeStages = newDC.activeStages.filter(
                s => s[0] !== FormStateConsts.AppointmentCapture
            );
        }
    }

    if (!toBoolean(newDC.websiteConfig.CaptureAssessmentType)) {
        newDC.activeStages = newDC?.activeStages?.filter(
            s => s[0] !== FormStateConsts.AssessmentTypeCapture
        );
    } else if (!newDC.websiteConfig.CaseID) {
        // When there is no case yet, the backend requires a case to exist
        // before it can handle assessment type selection. So we move
        // PersonCapture before AssessmentTypeCapture, allowing the user to
        // enter their details first which creates the case.
        const personIndex = newDC.activeStages.findIndex(
            s => s[0] === FormStateConsts.PersonCapture
        );
        const assessmentIndex = newDC.activeStages.findIndex(
            s => s[0] === FormStateConsts.AssessmentTypeCapture
        );
        if (personIndex > -1 && assessmentIndex > -1) {
            newDC.activeStages.splice(assessmentIndex, 0, newDC.activeStages.splice(personIndex, 1)[0]);
        }
    }

    // EarlyPayment places the payment step immediately before assessment type
    // selection. We anchor directly to AssessmentTypeCapture rather than
    // PersonCapture because PersonCapture may not be present (e.g. when a
    // CaseID already exists, PersonCapture is removed later).
    //
    // When there IS no CaseID, the block above has already moved PersonCapture
    // before AssessmentTypeCapture, so the resulting order is:
    //   ...PersonCapture, PaymentCapture, AssessmentTypeCapture...
    // This ensures person details are captured first (creating the case)
    // before payment is taken.
    //
    // When there IS a CaseID, PersonCapture will be removed later (line ~308)
    // and we just need PaymentCapture before AssessmentTypeCapture:
    //   ...PaymentCapture, AssessmentTypeCapture...
    if (toBoolean(newDC.websiteConfig.EarlyPayment) &&
        toBoolean(newDC.websiteConfig.CaptureAssessmentType)) {
        const paymentIndex = newDC.activeStages.findIndex(
            s => s[0] === FormStateConsts.PaymentCapture
        );
        const assessmentIndex = newDC.activeStages.findIndex(
            s => s[0] === FormStateConsts.AssessmentTypeCapture
        );
        if (paymentIndex > -1 && assessmentIndex > -1) {
            const [paymentStage] = newDC.activeStages.splice(paymentIndex, 1);
            const newAssessmentIndex = newDC.activeStages.findIndex(
                s => s[0] === FormStateConsts.AssessmentTypeCapture
            );
            newDC.activeStages.splice(newAssessmentIndex, 0, paymentStage);
        }
    }

    if (!toBoolean(newDC.websiteConfig.AdditionalData?.IsPaymentCardRequired)) {
        newDC.activeStages = newDC?.activeStages?.filter(
            s => s[0] !== FormStateConsts.PaymentCapture
        );
    }

    if (newDC.websiteConfig.CaseID) {
        newDC.caseID = newDC.websiteConfig.CaseID;
        newDC.CaseID = newDC.websiteConfig.CaseID;
        // We already have a case so we can't pick a client, service activity
        // or enter person details
        newDC.activeStages = newDC.activeStages.filter(
            s => (s[0] !== FormStateConsts.PersonCapture) 
            && (s[0] !== FormStateConsts.ServiceCapture)
            && (s[0] !== FormStateConsts.ClientCapture)
        );
    } else if (!newDC.websiteConfig.ReferralForm && !newDC.configMode) {
        newDC.pageState = FormStateConsts.Error;
        newDC.error = "You have arrived at this site through an invalid link.";
    }
    if (2 > newDC.websiteConfig.Clients?.length) {
        newDC.activeStages = newDC.activeStages.filter(
            s => s[0] !== FormStateConsts.ClientCapture
        );
    }
    if (2 > newDC.websiteConfig.ServiceActivities?.length) {
        newDC.activeStages = newDC.activeStages.filter(
            s => s[0] !== FormStateConsts.ServiceActivities
        );
    }
    if (newDC.digitalAssessment){
         newDC.activeStages = newDC.activeStages.filter(
            s => s[0] !== FormStateConsts.AssessmentTypeCapture
        );
    }
    newDC.Clients = newDC.websiteConfig.Clients;
    newDC.captureInjuries = newDC.websiteConfig.CaptureInjuries;
    newDC.serviceActivityId = newDC.websiteConfig.ServiceActivityId;

    if (!newDC.websiteConfig.Token && !newDC.configMode) {
        newDC.pageState = FormStateConsts.Error;
        newDC.error = "Please use the booking link sent to you by email.";
    }
};

export default updateWithConfig;